import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import ReactCodeMirror, { EditorView } from '@uiw/react-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { markdown } from '@codemirror/lang-markdown'
import { yaml } from '@codemirror/lang-yaml'
import { Box, Button, Divider, FormControlLabel, IconButton, Stack, Switch, TextField, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import RefreshIcon from '@mui/icons-material/Refresh'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { com } from '@eidu/form'
import AssetRenderer, { RENDER_HEIGHT, RENDER_WIDTH } from './AssetRenderer'
import { AssetTypeDefinition, type RenderData } from './assetTypes'
import { AssetEntityTypeConfig, useAssetTypeRegistry } from './useAssetTypeRegistry'
import AssetReferenceEditorDialog from './AssetChooserDialog'
import { extractFeaturePreviewParams } from './features/previewParams'
import {
  AssetReferenceMatch,
  AssetReferenceLabelInfo,
  findAssetReferences,
  findAssetReferenceAt,
  highlightAssetReferences,
} from '../../components/misc/assetReferences'
import PageHeaderActionLabel, { pageHeaderActionButtonSx } from '../../components/layout/PageHeaderActionLabel'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import getEntitiesBatch, { MAX_NUM_IDS_PER_BATCH } from '../../api/entity/data/getEntitiesBatch'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import getEntitiesFromResponses from '../../domain/entity/getEntitiesFromResponses'
import chunked from '../../util/chunked'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull

type AssetPageProps = {
  fieldsSection: ReactNode
  headerSection?: ReactNode
  payloadEnabled?: boolean
  payloadValue?: string
  onPayloadChange?: (value: string, options?: { fromAssistant?: boolean }) => void
  assetType?: AssetTypeDefinition
  renderData?: RenderData
  previewUpdateToken?: number
  actionsSection?: ReactNode
}

type PayloadSectionProps = {
  value: string
  onChange: (value: string) => void
  onAssetReferenceApplied?: (value: string) => void
  assetType?: AssetTypeDefinition
  assetEntityTypes: AssetEntityTypeConfig[]
  assetEntityTypeIds: string[]
  assetEntityTypesLoading: boolean
  syncToken?: number
}

type AssetReferenceLabelsConfig = {
  content: string
  enabled: boolean
}

const languageExtensions = {
  markdown: markdown(),
  yaml: yaml(),
  typescript: javascript({ typescript: true }),
}

const editorMinHeight = '240px'
const DEFAULT_PREVIEW_WIDTH = RENDER_WIDTH
const DEFAULT_PREVIEW_HEIGHT = Math.round(RENDER_HEIGHT)
const PREVIEW_MIN_SIZE = 200
const PREVIEW_MAX_SIZE = 1200
const PARAM_PREVIEW_ASSET_TYPE_IDS = new Set(['feature', 'learning_template'])

const getDefaultPreviewSize = (assetType?: AssetTypeDefinition) => {
  if (assetType?.previewSize) return assetType.previewSize
  return { width: DEFAULT_PREVIEW_WIDTH, height: DEFAULT_PREVIEW_HEIGHT }
}

const normalizeAssetId = (value: string) => value.trim().toLowerCase()

const useAssetReferenceLabels = ({
  content,
  enabled,
}: AssetReferenceLabelsConfig): ReadonlyMap<string, AssetReferenceLabelInfo> => {
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = useMemo(
    () => createAuthContext(iAuthContext),
    [iAuthContext.token, iAuthContext.idTokenData?.organization_id, iAuthContext.idTokenData?.user_entity_id]
  )
  const entityTypesRepo = useContext(EntityTypesContext)
  const entityTypes = entityTypesRepo.useAll()
  const [labelsById, setLabelsById] = useState<ReadonlyMap<string, AssetReferenceLabelInfo>>(new Map())
  const pendingIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    if (!enabled || !authContext || !entityTypes) return () => undefined

    const referencedIds = Array.from(
      new Set(
        findAssetReferences(content)
          .map((reference) => normalizeAssetId(reference.uuid))
          .filter(Boolean)
      )
    )
    if (referencedIds.length === 0) return () => undefined

    const missingIds = referencedIds.filter((id) => !labelsById.has(id) && !pendingIdsRef.current.has(id))
    if (missingIds.length === 0) return () => undefined

    missingIds.forEach((id) => pendingIdsRef.current.add(id))
    const entityIds = missingIds.map((id) => entityIdFromStringOrNull(id)).filter((id): id is EntityId => !!id)
    if (entityIds.length === 0) {
      missingIds.forEach((id) => pendingIdsRef.current.delete(id))
      return () => undefined
    }

    const load = async () => {
      try {
        const responses = await Promise.all(
          chunked(entityIds, MAX_NUM_IDS_PER_BATCH).map((batch) =>
            getEntitiesBatch({
              ids: batch,
              withRelatedEntities: true,
              pageIndex: 0,
              pageSize: batch.length,
              authContext,
            })
          )
        )
        if (cancelled) return

        const resolved = new Map<string, AssetReferenceLabelInfo>()
        const entities = getEntitiesFromResponses(responses, entityTypes)
        entities.forEach((entity) => {
          const id = normalizeAssetId(entity.entity.id.toString())
          const label = entity.primaryLabelText?.trim()
          resolved.set(id, {
            label: label && label.length > 0 ? label : null,
            typeId: entity.entity.typeId.toString(),
          })
        })

        missingIds.forEach((id) => {
          if (!resolved.has(id)) resolved.set(id, { label: null, typeId: null })
        })

        setLabelsById((previous) => {
          const next = new Map(previous)
          resolved.forEach((value, key) => next.set(key, value))
          return next
        })
      } catch {
        return
      } finally {
        missingIds.forEach((id) => pendingIdsRef.current.delete(id))
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [authContext, content, enabled, entityTypes, labelsById])

  return labelsById
}

const PayloadSection = ({
  value,
  onChange,
  onAssetReferenceApplied,
  assetType,
  assetEntityTypes,
  assetEntityTypeIds,
  assetEntityTypesLoading,
  syncToken,
}: PayloadSectionProps) => {
  const language = assetType?.contentLanguage ?? 'markdown'
  const extension = languageExtensions[language]
  const [assetReference, setAssetReference] = useState<AssetReferenceMatch | null>(null)
  const [editorValue, setEditorValue] = useState(value)
  const editorValueRef = useRef(value)
  const editorViewRef = useRef<EditorView | null>(null)
  const restoreScrollRef = useRef<{ editor?: number; window?: number } | null>(null)
  const resolvedExtensionRef = useRef(extension)
  const assetEntityTypeIdsKey = useMemo(() => assetEntityTypeIds.join('|'), [assetEntityTypeIds])
  const assetEntityTypeIdSet = useMemo(() => new Set(assetEntityTypeIds), [assetEntityTypeIdsKey])
  const assetReferenceLabels = useAssetReferenceLabels({
    content: editorValue,
    enabled: !assetEntityTypesLoading,
  })

  useEffect(() => {
    if (syncToken === undefined) return
    setEditorValue(value)
    editorValueRef.current = value
  }, [syncToken])

  useEffect(() => {
    resolvedExtensionRef.current = extension
  }, [])

  const closeAssetDialog = useCallback(() => {
    setAssetReference(null)
  }, [])

  const applyAssetReference = useCallback(
    (nextUuid: string) => {
      if (!assetReference) return
      const trimmed = nextUuid.trim()
      if (!trimmed) return
      const normalized = trimmed.startsWith('asset://') ? trimmed.slice('asset://'.length) : trimmed
      const replacement = `asset://${normalized}`
      const currentText = editorViewRef.current ? editorViewRef.current.state.doc.toString() : editorValueRef.current
      const currentValue = currentText.slice(assetReference.from, assetReference.to)
      if (currentValue === replacement) {
        closeAssetDialog()
        return
      }
      const editorView = editorViewRef.current
      if (editorView) {
        restoreScrollRef.current = {
          editor: editorView.scrollDOM.scrollTop,
          window: window.scrollY,
        }
        editorView.dispatch({
          changes: { from: assetReference.from, to: assetReference.to, insert: replacement },
        })
        const nextValue = editorView.state.doc.toString()
        setEditorValue(nextValue)
        editorValueRef.current = nextValue
        onChange(nextValue)
        onAssetReferenceApplied?.(nextValue)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const view = editorViewRef.current
            const restore = restoreScrollRef.current
            if (restore) {
              if (typeof restore.window === 'number') window.scrollTo({ top: restore.window })
              if (view && typeof restore.editor === 'number') view.scrollDOM.scrollTop = restore.editor
              restoreScrollRef.current = null
            }
          })
        })
      } else {
        const updatedValue =
          currentText.slice(0, assetReference.from) + replacement + currentText.slice(assetReference.to)
        setEditorValue(updatedValue)
        editorValueRef.current = updatedValue
        onChange(updatedValue)
        onAssetReferenceApplied?.(updatedValue)
      }
      closeAssetDialog()
    },
    [assetReference, closeAssetDialog, editorValue, onAssetReferenceApplied, onChange]
  )

  const assetReferenceExtensions = useMemo(() => {
    const highlightExtension = EditorView.decorations.of((view) =>
      highlightAssetReferences(view, assetReferenceLabels, assetEntityTypeIdSet)
    )
    const themeExtension = EditorView.theme({
      '.cm-asset-reference, .cm-asset-reference-pending': {
        cursor: 'pointer',
        textDecoration: 'underline dotted',
      },
      '.cm-asset-reference:hover, .cm-asset-reference-pending:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.15)',
      },
      '.cm-asset-reference-pending': {
        opacity: 0.7,
      },
    })
    const clickExtension = EditorView.domEventHandlers({
      click: (event, view) => {
        const target = event.target
        const element = target instanceof HTMLElement ? target : target instanceof Text ? target.parentElement : null
        if (!element) return false
        const assetElement = element.closest('.cm-asset-reference, .cm-asset-reference-pending')
        if (!assetElement) return false
        const assetId = assetElement.getAttribute('data-asset-id')
        const fromAttr = assetElement.getAttribute('data-asset-from')
        const toAttr = assetElement.getAttribute('data-asset-to')
        if (assetId && fromAttr && toAttr) {
          const from = Number(fromAttr)
          const to = Number(toAttr)
          if (Number.isFinite(from) && Number.isFinite(to)) {
            setAssetReference({ from, to, uuid: assetId })
            event.preventDefault()
            return true
          }
        }
        const pos = view.posAtDOM(assetElement, 0)
        const match = findAssetReferenceAt(view.state.doc.toString(), pos)
        if (!match) return false
        setAssetReference(match)
        event.preventDefault()
        return true
      },
    })
    return [highlightExtension, themeExtension, clickExtension]
  }, [assetReferenceLabels, assetEntityTypeIdSet])
  const editorExtensions = useMemo(
    () => [resolvedExtensionRef.current, ...assetReferenceExtensions],
    [assetReferenceExtensions]
  )
  const editorSetup = useMemo(() => ({ lineNumbers: true }), [])

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <ReactCodeMirror
          value={editorValue}
          onChange={(nextValue) => {
            editorValueRef.current = nextValue
            onChange(nextValue)
          }}
          extensions={editorExtensions}
          basicSetup={editorSetup}
          minHeight={editorMinHeight}
          height="100%"
          onCreateEditor={(view) => {
            editorViewRef.current = view
          }}
        />
      </Box>
      {assetReference && (
        <AssetReferenceEditorDialog
          assetReference={assetReference}
          onClose={closeAssetDialog}
          onSelectAsset={applyAssetReference}
          assetEntityTypes={assetEntityTypes}
          assetEntityTypesLoading={assetEntityTypesLoading}
          currentAssetType={assetType}
        />
      )}
    </Stack>
  )
}

const AssetPage = ({
  fieldsSection,
  headerSection,
  payloadEnabled = false,
  payloadValue = '',
  onPayloadChange,
  assetType,
  renderData,
  previewUpdateToken,
  actionsSection,
}: AssetPageProps) => {
  const previewInitializedRef = useRef(false)
  const [previewWidth, setPreviewWidth] = useState(DEFAULT_PREVIEW_WIDTH)
  const [previewHeight, setPreviewHeight] = useState(DEFAULT_PREVIEW_HEIGHT)
  const [previewWidthInput, setPreviewWidthInput] = useState(String(DEFAULT_PREVIEW_WIDTH))
  const [previewHeightInput, setPreviewHeightInput] = useState(String(DEFAULT_PREVIEW_HEIGHT))
  const [previewReloadToken, setPreviewReloadToken] = useState(0)
  const [previewPayload, setPreviewPayload] = useState(payloadValue ?? '')
  const [previewRenderData, setPreviewRenderData] = useState(renderData)
  const [previewAssetType, setPreviewAssetType] = useState(assetType)
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false)
  const [isPreviewAutoUpdateEnabled, setIsPreviewAutoUpdateEnabled] = useState(false)

  const entityTypesRepo = useContext(EntityTypesContext)
  const entityTypes = entityTypesRepo.useAll() ?? new Map<EntityTypeId, EntityType>()
  const { assetEntityTypes, loading: assetEntityTypesLoading } = useAssetTypeRegistry(entityTypes)
  const referenceAssetEntityTypes = assetEntityTypes
  const assetEntityTypeConfigsById = useMemo(
    () => new Map(assetEntityTypes.map((config) => [config.entityTypeId.toString(), config] as const)),
    [assetEntityTypes]
  )
  const assetEntityTypeIds = useMemo(
    () => assetEntityTypes.map((config) => config.entityTypeId.toString()).sort(),
    [assetEntityTypes]
  )

  const reloadPreview = useCallback(
    (nextPayload?: string) => {
      setPreviewPayload(nextPayload ?? payloadValue ?? '')
      setPreviewRenderData(renderData)
      setPreviewAssetType(assetType)
      setPreviewReloadToken((prev) => prev + 1)
    },
    [assetType, payloadValue, renderData]
  )

  useEffect(() => {
    if (previewUpdateToken === undefined) return
    reloadPreview()
  }, [previewUpdateToken])

  useEffect(() => {
    if (previewInitializedRef.current) return
    if (!assetType) return
    const defaults = getDefaultPreviewSize(assetType)
    setPreviewWidth(defaults.width)
    setPreviewHeight(defaults.height)
    setPreviewWidthInput(String(defaults.width))
    setPreviewHeightInput(String(defaults.height))
    previewInitializedRef.current = true
  }, [assetType])

  const clampPreviewSize = useCallback(
    (value: number) => Math.min(PREVIEW_MAX_SIZE, Math.max(PREVIEW_MIN_SIZE, Math.round(value))),
    []
  )

  const handleWidthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value
      setPreviewWidthInput(nextValue)
      if (!nextValue.trim()) return
      const parsed = Number(nextValue)
      if (!Number.isFinite(parsed)) return
      setPreviewWidth(clampPreviewSize(parsed))
    },
    [clampPreviewSize]
  )

  const handleHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value
      setPreviewHeightInput(nextValue)
      if (!nextValue.trim()) return
      const parsed = Number(nextValue)
      if (!Number.isFinite(parsed)) return
      setPreviewHeight(clampPreviewSize(parsed))
    },
    [clampPreviewSize]
  )

  const syncWidthInput = useCallback(() => {
    setPreviewWidthInput(String(previewWidth))
  }, [previewWidth])

  const syncHeightInput = useCallback(() => {
    setPreviewHeightInput(String(previewHeight))
  }, [previewHeight])

  const handleSwapDimensions = useCallback(() => {
    const nextWidth = previewHeight
    const nextHeight = previewWidth
    setPreviewWidth(nextWidth)
    setPreviewHeight(nextHeight)
    setPreviewWidthInput(String(nextWidth))
    setPreviewHeightInput(String(nextHeight))
  }, [previewHeight, previewWidth])

  const handleReloadPreview = useCallback(() => {
    reloadPreview()
  }, [reloadPreview])

  const openPreviewFullscreen = useCallback(async () => {
    setIsPreviewFullscreen(true)
    if (typeof document === 'undefined') return
    if (!document.documentElement.requestFullscreen) return
    try {
      await document.documentElement.requestFullscreen()
    } catch {
      // Ignore fullscreen errors and fall back to the overlay.
    }
  }, [])

  const closePreviewFullscreen = useCallback(() => {
    setIsPreviewFullscreen(false)
    if (typeof document === 'undefined') return
    if (!document.fullscreenElement) return
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => undefined)
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsPreviewFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  useEffect(() => {
    if (!isPreviewFullscreen) return undefined
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      closePreviewFullscreen()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [closePreviewFullscreen, isPreviewFullscreen])

  useEffect(() => {
    if (!isPreviewFullscreen || typeof document === 'undefined') return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isPreviewFullscreen])

  const handlePayloadEditorChange = useCallback(
    (value: string) => {
      onPayloadChange?.(value, { fromAssistant: false })
      if (isPreviewAutoUpdateEnabled) {
        reloadPreview(value)
      }
    },
    [isPreviewAutoUpdateEnabled, onPayloadChange, reloadPreview]
  )

  const handleAutoUpdateChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsPreviewAutoUpdateEnabled(checked)
      if (checked) {
        reloadPreview(payloadValue ?? '')
      }
    },
    [payloadValue, reloadPreview]
  )

  useEffect(() => {
    const handleHotkey = (event: KeyboardEvent) => {
      if (!event.ctrlKey || event.metaKey) return
      if (event.shiftKey || event.altKey) return
      if (event.key !== 'Enter') return
      event.preventDefault()
      handleReloadPreview()
    }
    window.addEventListener('keydown', handleHotkey)
    return () => {
      window.removeEventListener('keydown', handleHotkey)
    }
  }, [handleReloadPreview])

  const payloadSection =
    payloadEnabled && onPayloadChange ? (
      <PayloadSection
        value={payloadValue}
        onChange={handlePayloadEditorChange}
        onAssetReferenceApplied={reloadPreview}
        assetType={assetType}
        assetEntityTypes={referenceAssetEntityTypes}
        assetEntityTypeIds={assetEntityTypeIds}
        assetEntityTypesLoading={assetEntityTypesLoading}
        syncToken={previewUpdateToken}
      />
    ) : (
      <Typography color="text.secondary">No payload editor available for this type.</Typography>
    )

  const previewParams = useMemo(
    () =>
      previewAssetType?.id && PARAM_PREVIEW_ASSET_TYPE_IDS.has(previewAssetType.id)
        ? extractFeaturePreviewParams(previewPayload ?? '')
        : undefined,
    [previewAssetType?.id, previewPayload]
  )

  const renderPreview = (layout: 'fixed' | 'fill') => {
    const isFillLayout = layout === 'fill'
    const previewSx = isFillLayout
      ? { border: 'none', borderRadius: 0 }
      : {
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }
    const previewWidthValue = isFillLayout ? undefined : previewWidth
    const previewHeightValue = isFillLayout ? undefined : previewHeight

    return (
      <AssetRenderer
        content={previewPayload ?? ''}
        assetType={previewAssetType}
        assetEntityTypeConfigsById={assetEntityTypeConfigsById}
        renderData={previewRenderData}
        previewWidth={previewWidthValue}
        previewHeight={previewHeightValue}
        initialParams={previewParams}
        reloadToken={previewReloadToken}
        previewSx={previewSx}
        layout={isFillLayout ? 'fill' : 'fixed'}
        showLog={!isFillLayout}
      />
    )
  }

  const fullscreenOverlay =
    isPreviewFullscreen && typeof document !== 'undefined'
      ? createPortal(
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 10,
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <IconButton
              aria-label="Close fullscreen preview"
              onClick={closePreviewFullscreen}
              sx={{
                position: 'absolute',
                top: 'calc(16px + env(safe-area-inset-top))',
                right: 'calc(16px + env(safe-area-inset-right))',
                zIndex: 2,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.8)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                minHeight: 0,
              }}
            >
              {renderPreview('fill')}
            </Box>
          </Box>,
          document.body
        )
      : null

  return (
    <>
      <Stack spacing={3} sx={{ height: '100%', minHeight: 0 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            {headerSection ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ flexShrink: 0, minWidth: 0 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>{headerSection}</Box>
                <Box sx={{ display: { xs: 'flex', lg: 'none' }, flexShrink: 0 }}>
                  <Button
                    color="primary"
                    startIcon={<FullscreenIcon />}
                    onClick={openPreviewFullscreen}
                    aria-label="Full Screen"
                    sx={pageHeaderActionButtonSx}
                  >
                    <PageHeaderActionLabel>Full Screen</PageHeaderActionLabel>
                  </Button>
                </Box>
              </Stack>
            ) : (
              <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'flex-end' }}>
                <Button
                  color="primary"
                  startIcon={<FullscreenIcon />}
                  onClick={openPreviewFullscreen}
                  aria-label="Full Screen"
                  sx={pageHeaderActionButtonSx}
                >
                  <PageHeaderActionLabel>Full Screen</PageHeaderActionLabel>
                </Button>
              </Box>
            )}
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 1 }}>
              {fieldsSection}
              <Box sx={{ flex: 1, minHeight: 0 }}>{payloadSection}</Box>
            </Stack>
            {actionsSection && (
              <Box
                sx={{
                  pt: 1,
                  bgcolor: 'background.default',
                  flexShrink: 0,
                }}
              >
                {actionsSection}
              </Box>
            )}
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' } }} />
          <Box
            sx={{
              flexShrink: 0,
              minWidth: 0,
              width: { xs: '100%', lg: `${previewWidth}px` },
              minHeight: 0,
              display: { xs: 'none', lg: 'block' },
            }}
          >
            <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%' }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton aria-label="Reload preview" size="small" onClick={handleReloadPreview}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="Open fullscreen preview" size="small" onClick={openPreviewFullscreen}>
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                  <FormControlLabel
                    control={
                      <Switch size="small" checked={isPreviewAutoUpdateEnabled} onChange={handleAutoUpdateChange} />
                    }
                    label="Auto update"
                    sx={{
                      ml: 0.5,
                      mr: 0,
                      '.MuiFormControlLabel-label': {
                        color: 'text.secondary',
                        fontSize: 13,
                      },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <TextField
                    size="small"
                    type="number"
                    value={previewWidthInput}
                    onChange={handleWidthChange}
                    onBlur={syncWidthInput}
                    inputProps={{ min: PREVIEW_MIN_SIZE, max: PREVIEW_MAX_SIZE }}
                    sx={{ width: 90 }}
                  />
                  <Typography color="text.secondary">x</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={previewHeightInput}
                    onChange={handleHeightChange}
                    onBlur={syncHeightInput}
                    inputProps={{ min: PREVIEW_MIN_SIZE, max: PREVIEW_MAX_SIZE }}
                    sx={{ width: 90 }}
                  />
                  <IconButton aria-label="Swap preview dimensions" size="small" onClick={handleSwapDimensions}>
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0 }}>{renderPreview('fixed')}</Box>
            </Stack>
          </Box>
        </Stack>
      </Stack>
      {fullscreenOverlay}
    </>
  )
}

export default AssetPage
